import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { DrawerTopBarWithAction } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { HotelClassFilter } from "@/features/hotels/components/hotels-list/HotelClassFilter";
import { HOTEL_CLASSES } from "@/features/hotels/utils/constants";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";

type HotelClassFilterContentsProps = {
  defaultFilter?: number[];
  value?: number[];
  onSubmit: (newValue: number[]) => void;
  onCloseClick: () => void;
};

export const HotelClassFilterContents = ({
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: HotelClassFilterContentsProps) => {
  const intl = useIntl();

  const [localFilter, setLocalFilter] = useState(value);

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<number>({
    selected: localFilter,
    all: HOTEL_CLASSES,
  });

  const isInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onCloseClick}
        title={intl.formatMessage({
          id: "hotels.filters.hotelClass",
          defaultMessage: "Hotel Class",
        })}
        actionElement={
          <Button
            onClick={(e) => {
              if (isInDefaultState) {
                setLocalFilter(HOTEL_CLASSES);
                setManuallySelectedAll(true);
              } else {
                setLocalFilter(defaultFilter);
                setManuallySelectedAll(false);
              }

              // Remove focus from the button after click
              e.currentTarget.blur();
            }}
            variant="text"
            size="small"
          >
            {isInDefaultState
              ? intl.formatMessage({
                  id: "selectAll",
                  defaultMessage: "Select All",
                })
              : intl.formatMessage({
                  id: "clearFilter",
                  defaultMessage: "Clear filter",
                })}
          </Button>
        }
      />

      <HotelClassFilter
        selectedClasses={localFilter}
        userManuallySelectedAllClasses={manuallySelectedAll}
        onChange={(_e, checked, option) => {
          setLocalFilter((prevFilter) => {
            const newClasses = toggle(prevFilter, option, checked);
            return newClasses.sort((a, b) => a - b);
          });
        }}
      />

      <div className="mt-18">
        <Button
          fullWidth
          onClick={() => {
            onSubmit(localFilter);
            onCloseClick();
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({ id: "apply", defaultMessage: "Apply" })}
        </Button>
      </div>
    </>
  );
};

type Props = HotelClassFilterContentsProps & {
  open: boolean;
};

export const HotelClassFilterDrawer = ({
  open,
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onCloseClick}>
      {/* need to reset the state when drawer is open/closed */}
      {open ? (
        <HotelClassFilterContents
          defaultFilter={defaultFilter}
          value={value}
          onSubmit={onSubmit}
          onCloseClick={onCloseClick}
        />
      ) : null}
    </SwipeableDrawer>
  );
};
